import { useEffect, useId, useState, type ReactNode } from 'react'
import type { ReviewEntry } from './types'
import type { usePeerReviewDraft } from './usePeerReviewDraft'
import { ReviewResizeHandle } from './ReviewResizeHandle'

interface Props {
  id?: string
  quality: boolean
  readOnly?: boolean
  reviewPicker?: ReactNode
  busy: boolean
  submitted: boolean
  ownReview?: Pick<ReviewEntry, 'entry_id' | 'verdict' | 'feedback'>
  draft: ReturnType<typeof usePeerReviewDraft>['draft']
  update: ReturnType<typeof usePeerReviewDraft>['update']
  submit: () => Promise<void>
  title?: string
  submitLabel?: string
  readyDisabled?: boolean
  feedbackRequired?: boolean
  submitDisabled?: boolean
  notReadyLabel?: string
  hideFeedbackLabel?: boolean
  children?: ReactNode
}

/** One explicit submission action, shared by peer and quality reviews. */
export function ReviewDecision({
  id: anchorId,
  quality,
  readOnly = false,
  reviewPicker,
  busy,
  submitted,
  ownReview,
  draft,
  update,
  submit,
  children,
  title = 'Your review',
  submitLabel,
  readyDisabled = false,
  feedbackRequired = true,
  submitDisabled = false,
  notReadyLabel = 'Needs changes',
  hideFeedbackLabel = false,
}: Props) {
  const id = useId()
  const verdict = readOnly ? ownReview?.verdict ?? '' : draft.verdict
  const feedback = readOnly ? ownReview?.feedback ?? '' : draft.feedback
  const needsFeedback = feedbackRequired && verdict === 'needs_changes'
  const [editing, setEditing] = useState(!!draft.verdict || !!draft.feedback)
  useEffect(() => { if (ownReview && !verdict && !feedback) setEditing(false) }, [ownReview?.entry_id, verdict, feedback])
  const editReview = <button className="link small" disabled={busy} onClick={() => {
    update('verdict', ownReview?.verdict ?? ''); update('feedback', ownReview?.feedback ?? ''); setEditing(true)
  }}>Edit review</button>
  if (ownReview && !editing && !readOnly) return <aside id={anchorId} className="peer-decision" aria-label="Your submitted review">
    <ReviewResizeHandle />
    <header className="peer-decision-heading">
      <div className="peer-decision-title">
        <h3>{title}</h3>
        <span className={`chip ${ownReview.verdict === 'ready' ? 'ok' : 'warn'}`}>{ownReview.verdict === 'ready' ? 'Ready' : 'Needs changes'}</span>
      </div>
      {reviewPicker}
    </header>
    {editReview}
    {ownReview.feedback?.trim() && <p className="prewrap">{ownReview.feedback}</p>}
    {children}
  </aside>
  return (
    <aside id={anchorId} className="peer-decision" aria-label={readOnly ? 'Your previous review' : 'Your review decision'}>
      <ReviewResizeHandle />
      <header className="peer-decision-heading"><h3>{title}</h3>{reviewPicker}</header>
      {readOnly && <p className="small muted">Read-only · {ownReview ? 'Submitted review' : 'No review submitted'}</p>}
      {!readOnly && (ownReview || submitted) && (
        <div className="small review-submitted" role="status">
          <strong>Review submitted</strong>
        </div>
      )}
      <fieldset className="peer-verdict" disabled={busy || readOnly}>
        <legend className="visually-hidden">Review outcome</legend>
        <label className={readyDisabled ? 'verdict-unavailable' : undefined}>
          <input
            type="radio"
            name={`${id}-verdict`}
            disabled={readyDisabled}
            checked={verdict === 'ready'}
            onChange={() => update('verdict', 'ready')}
          />
          {quality ? 'Ready' : 'Ready for build'}
        </label>
        <label>
          <input
            type="radio"
            name={`${id}-verdict`}
            checked={verdict === 'needs_changes'}
            onChange={() => update('verdict', 'needs_changes')}
          />
          {notReadyLabel}
        </label>
      </fieldset>
      <label className={hideFeedbackLabel ? 'visually-hidden' : undefined} htmlFor={`${id}-feedback`}>
        {quality ? 'Your feedback' : 'Feedback for the author'}
        {!readOnly && verdict === 'needs_changes' ? ' (required)' : ''}
      </label>
      <textarea
        id={`${id}-feedback`}
        disabled={busy}
        readOnly={readOnly}
        value={feedback}
        onChange={(event) => update('feedback', event.target.value)}
        rows={10}
        maxLength={8000}
        placeholder={readOnly ? 'No written feedback.' : 'What works, or what needs to change?'}
      />
      {!readOnly && <div className="peer-decision-actions">
        <button
          className="primary"
          disabled={
            busy || submitDisabled || !verdict || (needsFeedback && !feedback.trim())
          }
          onClick={() => void submit()}
        >
          {busy
            ? 'Submitting…'
            : (submitLabel ?? (ownReview || submitted ? 'Update review' : 'Submit review'))}
        </button>
        {ownReview && <button className="link" disabled={busy} onClick={() => {
          update('verdict', ''); update('feedback', ''); setEditing(false)
        }}>Cancel editing</button>}
      </div>}
      {children}
    </aside>
  )
}
